import { PRODUCT_ID } from '@shared/support-bundles/contract'
import type { HydraulicSystem } from '@shared/hydraulic/system'

export interface ModelInput {
  system: HydraulicSystem | null
  calculationResults: unknown
}

export interface SerializedModel {
  source: typeof PRODUCT_ID
  schema_version: number
  /**
   * The whole document.
   *
   * Named `model`, not `system`, per CONTEXT.md: a Model is the document an
   * Engineer opens, while a System is one independently-calculated network
   * *within* it. The in-memory type is still called `HydraulicSystem` for
   * historical reasons, and propagating that name into a new artifact — one a
   * separate service stores and support staff read — is how "System" came to
   * mean three different things in the first place.
   */
  model: HydraulicSystem
  calculation_result?: unknown
}

/**
 * Serialises the open document for the bundle.
 *
 * Only the document and its solver results. The store holds far more that looks
 * tempting — `scenarioResults` keeps a fully materialised copy of the system per
 * scenario, and the undo/redo stacks are fifty unshared deep copies, hundreds of
 * megabytes on a large model — none of which ships.
 *
 * The system goes inside an envelope rather than being spread alongside our own
 * fields. `handleExportJson` spreads a `source` key and documents that it is
 * silently stripped on re-import: `HydraulicSystemSchema` is non-strict, so an
 * unknown key survives `safeParse` but is dropped from `.data`, which consumers
 * read. An envelope keeps our fields out of the parsed object entirely, so
 * neither side can lose them.
 */
export function serializeModel(input: ModelInput): SerializedModel | null {
  const { system, calculationResults } = input
  if (!system) return null

  return {
    source: PRODUCT_ID,
    schema_version: system.schema_version,
    model: system,
    ...(calculationResults != null && { calculation_result: calculationResults }),
  }
}
